import { reportError } from "@utils/errorReporting";

type SafeJsonParseContext = {
	component?: string;
	action?: string;
	metadata?: Record<string, unknown>;
};

export type SafeJsonParseResult<T> =
	| {
			success: true;
			value: T;
	  }
	| {
			success: false;
			value: T;
			error: unknown;
	  };

const reportedParseFailures = new Set<string>();

const getParseErrorKey = (
	context: SafeJsonParseContext | undefined,
	value: string,
) =>
	`${context?.component ?? "unknown"}:${context?.action ?? "unknown"}:${value.length}`;

const reportParseErrorOnce = (
	error: unknown,
	context: SafeJsonParseContext | undefined,
	value: string,
) => {
	const errorKey = getParseErrorKey(context, value);

	if (reportedParseFailures.has(errorKey)) {
		return;
	}

	if (reportedParseFailures.size > 500) {
		reportedParseFailures.clear();
	}

	reportedParseFailures.add(errorKey);
	reportError(error, {
		component: context?.component ?? "SafeJsonParse",
		action: context?.action ?? "parse_json",
		metadata: {
			...(context?.metadata ?? {}),
			inputLength: value.length,
		},
	});
};

export const safeJsonParse = <T>(
	value: string,
	fallbackValue: T,
	context?: SafeJsonParseContext,
): SafeJsonParseResult<T> => {
	try {
		return {
			success: true,
			value: JSON.parse(value) as T,
		};
	} catch (error) {
		reportParseErrorOnce(error, context, value);
		return {
			success: false,
			value: fallbackValue,
			error,
		};
	}
};
